import { IconCircleOff, IconCheck, IconExchange } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import './i18n';
import { Button } from '@hana/ui';
import s from './pdfSign.module.css';

// Mint-FileViewer · mode=sign (Ký / đóng dấu PDF): thanh đầu "Ký / đóng dấu · trang N" + Huỷ / Áp dụng chữ ký;
// cột phải: 3 loại chữ ký, trang, vị trí 3×2, cỡ 80–220 pt. Mộc + chữ ký Giám đốc cần quyền riêng. Lưu ra tệp: toast "Chưa mở ghi ở bản này".

export type SignType = 'mine' | 'ceo' | 'surr';
export type SignPos = 'tl' | 'tc' | 'tr' | 'bl' | 'bc' | 'br';
export interface SignState { type: SignType; page: number; pos: SignPos; size: number }
export const defaultSign = (pages: number): SignState => ({ type: 'mine', page: Math.max(0, pages - 1), pos: 'br', size: 140 });

const OPTS: { k: SignType; ic: string }[] = [
  { k: 'mine', ic: 'KÝ' },
  { k: 'ceo', ic: 'MỘC' },
  { k: 'surr', ic: 'SURR' },
];
const POSK: SignPos[] = ['tl', 'tc', 'tr', 'bl', 'bc', 'br'];
const TYPE_CLS: Record<SignType, string> = { mine: s.tMine ?? '', ceo: s.tCeo ?? '', surr: s.tSurr ?? '' };

export function SignBar({ page, onCancel, onApply }: { page: number; onCancel: () => void; onApply: () => void }) {
  const t = useT();
  return (
    <>
      <span className={s.barK}>{t('fv.signBar', { n: page + 1 })}</span>
      <Button variant="ghost" icon={<IconCircleOff size={16} />} onClick={onCancel}>{t('fv.cancel')}</Button>
      <Button variant="primary" icon={<IconCheck size={16} />} onClick={onApply}>{t('fv.signApply')}</Button>
    </>
  );
}

export function StampMark({ v }: { v: SignState }) {
  const t = useT();
  const surr = v.type === 'surr', ceo = v.type === 'ceo';
  const w = ceo ? v.size : surr ? v.size + 40 : v.size;
  const h = ceo ? v.size : Math.round(v.size * (surr ? 0.42 : 0.45));
  const fs = Math.round(v.size / (surr ? 7 : ceo ? 10 : 6));
  return (
    <div role="img" aria-label={t('fv.signPosOf', { x: surr ? t('fv.signSurr') : t('fv.signSig') })} className={`${s.stamp} ${TYPE_CLS[v.type]} ${s[v.pos] ?? ''}`} style={{ width: w, height: h, marginLeft: v.pos === 'tc' || v.pos === 'bc' ? -Math.round(w / 2) : undefined }}>
      <span style={{ fontSize: fs }}>{surr ? 'SURRENDERED' : ceo ? 'HANA LOGISTICS' : 'DAVID'}</span>
      <span className={s.stSub}>{surr ? 'HANA LOGISTICS · 22 OCT 2026' : ceo ? t('fv.signDir') : t('fv.signedOn')}</span>
    </div>
  );
}

export function SignPanel({ v, onChange, n, name, onToast }: { v: SignState; onChange: (v: SignState) => void; n: number; name: string; onToast: (m: string) => void }) {
  const t = useT();
  return (
    <aside className={s.side} aria-label={t('fv.rightCol')}>
      <b className={s.h}>{t('fv.signTitle')}</b>
      <div role="radiogroup" aria-label={t('fv.signKind')} className={s.opts}>
        {OPTS.map((o) => (
          <button key={o.k} type="button" role="radio" aria-checked={v.type === o.k} className={s.opt} onClick={() => onChange({ ...v, type: o.k })}>
            <span aria-hidden="true" className={`${s.ic} ${TYPE_CLS[o.k]}`}>{o.ic}</span>
            <span className={s.optTx}><b>{t(`fv.sg.${o.k}.l`)}</b><span>{t(`fv.sg.${o.k}.v`)}</span></span>
          </button>
        ))}
      </div>
      {v.type === 'mine' && <button type="button" className={s.chg} onClick={() => onToast(t('fv.sigSwapped'))}><IconExchange size={16} aria-hidden="true" />{t('fv.sigSwap')}</button>}
      <div>
        <div className={s.lbl}>{t('fv.page')}</div>
        <div className={s.seg}>{Array.from({ length: n }, (_, i) => <button key={i} type="button" aria-pressed={v.page === i} onClick={() => onChange({ ...v, page: i })}>{i + 1}</button>)}</div>
      </div>
      <div>
        <div className={s.lbl}>{t('fv.position')}</div>
        <div className={s.grid}>{POSK.map((k) => <button key={k} type="button" aria-pressed={v.pos === k} onClick={() => onChange({ ...v, pos: k })}>{t(`fv.pos.${k}`)}</button>)}</div>
      </div>
      <label className={s.sz}>
        <div className={s.lbl}><span>{t('fv.size')}</span><span>{v.size} pt</span></div>
        <input type="range" min={80} max={220} step={10} value={v.size} aria-label={t('fv.sizeSig')} onChange={(e) => onChange({ ...v, size: Math.max(80, Math.min(220, Number(e.target.value) || 140)) })} />
      </label>
      <div className={s.note}>{t('fv.saveOut')}<b>{name}</b>{t('fv.saveBeside')}</div>
    </aside>
  );
}
